import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type FoundationalTaskInsert = Database['public']['Tables']['foundational_tasks']['Insert'];

// Hardcoded GrowthOS brand - all errors go here
const GROWTHOS_BRAND_ID = 'a37d3c2d-29a1-42ff-8d79-46e90ffc39d1';

// Hardcoded squad member for error task assignment (Theo Moulos)
const GROWTHOS_ERROR_ASSIGNEE_ID = '5b2f20e0-e9d0-48e4-aa87-e5abcfb6cca7';

interface ErrorDetails {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
}

/**
 * Creates a foundational task from an error if no duplicate exists in the last 24 hours
 */
export async function createErrorTask(error: Error, errorInfo?: React.ErrorInfo): Promise<void> {
  try {
    // Get current user for tracking who triggered the error
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return; // Skip if no user is logged in

    const errorMessage = error.message || 'Unknown error';
    
    // Check for duplicate error in last 24 hours
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

    const { data: existingTask } = await supabase
      .from('foundational_tasks')
      .select('id')
      .eq('brand_id', GROWTHOS_BRAND_ID)
      .eq('source_type', 'error')
      .gte('created_at', twentyFourHoursAgo.toISOString())
      .contains('source_data', { error_message: errorMessage })
      .maybeSingle();

    if (existingTask) {
      console.log('Duplicate error task detected, skipping creation');
      return;
    }

    // Prepare error details
    const errorDetails: ErrorDetails = {
      message: errorMessage,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };

    // Create task with hardcoded assignee (Theo Moulos)
    const taskData: FoundationalTaskInsert = {
      title: `Error: ${errorMessage.substring(0, 100)}`,
      description: `An error occurred in the application:\n\n**Error Message:** ${errorMessage}\n\n**Page:** ${window.location.href}\n\n**Stack Trace:**\n\`\`\`\n${error.stack || 'No stack trace available'}\n\`\`\``,
      priority: 'High',
      difficulty: 'Medium',
      assigned_to: GROWTHOS_ERROR_ASSIGNEE_ID,
      brand_id: GROWTHOS_BRAND_ID,
      user_id: user.id,
      source_type: 'error',
      source_data: errorDetails as any,
      status: 'pending'
    };

    const { error: insertError } = await supabase
      .from('foundational_tasks')
      .insert(taskData);

    if (insertError) {
      console.error('Failed to create error task:', insertError);
    }
  } catch (err) {
    // Don't throw errors from error handler to avoid infinite loops
    console.error('Failed to create error task:', err);
  }
}
